import { create } from 'zustand'
import { Page, ComponentBlock, SEOMetadata, ComponentProps } from '@/types/database'
import { v4 as uuidv4 } from 'uuid'

interface PageEditorState {
  // Current page being edited
  currentPage: Page | null
  setCurrentPage: (page: Page | null) => void
  
  // UI state
  isPreviewMode: boolean
  setPreviewMode: (isPreview: boolean) => void
  
  // Component selection
  selectedComponentId: string | null
  setSelectedComponent: (componentId: string | null) => void
  
  // Component editing state
  isEditingComponent: boolean
  setEditingComponent: (isEditing: boolean) => void
  finishEditingComponent: () => void
  
  // Dirty state tracking
  isDirty: boolean
  setDirty: (dirty: boolean) => void
  
  // Component actions
  addComponent: (type: string, afterIndex?: number, defaultProps?: ComponentProps) => void
  removeComponent: (componentId: string) => void
  updateComponent: (componentId: string, props: ComponentProps) => void
  reorderComponent: (componentId: string, newIndex: number) => void
  
  // Page settings actions
  updatePageSettings: (settings: {
    title?: string
    slug?: string
    is_private?: boolean
    seo?: SEOMetadata
  }) => void
}

export const usePageEditorStore = create<PageEditorState>((set, get) => ({
  // Initial state
  currentPage: null,
  isPreviewMode: false,
  selectedComponentId: null,
  isEditingComponent: false,
  isDirty: false,
  
  // State setters
  setCurrentPage: (page) => set({ currentPage: page, isDirty: false }),
  setPreviewMode: (isPreview) => set({ isPreviewMode: isPreview }),
  setSelectedComponent: (componentId) => set({ selectedComponentId: componentId }),
  setEditingComponent: (isEditing) => set({ isEditingComponent: isEditing }),
  finishEditingComponent: () => set({ isEditingComponent: false, selectedComponentId: null }),
  setDirty: (dirty) => set({ isDirty: dirty }),
  
  // Component actions
  addComponent: (type, afterIndex, defaultProps = {}) => {
    const { currentPage } = get()
    if (!currentPage) return
    
    const newComponent: ComponentBlock = {
      id: uuidv4(),
      type,
      props: defaultProps,
      order: afterIndex !== undefined ? afterIndex + 1 : currentPage.components.length
    }
    
    const updatedComponents = [...currentPage.components]
    if (afterIndex !== undefined) {
      updatedComponents.splice(afterIndex + 1, 0, newComponent)
      // Reorder all components
      updatedComponents.forEach((comp, index) => {
        comp.order = index
      })
    } else {
      updatedComponents.push(newComponent)
    }
    
    set({
      currentPage: { ...currentPage, components: updatedComponents },
      selectedComponentId: newComponent.id, // Auto-select the new component
      isEditingComponent: true, // Automatically start editing the new component
      isDirty: true
    })
  },
  
  removeComponent: (componentId) => {
    const { currentPage } = get()
    if (!currentPage) return
    
    const updatedComponents = currentPage.components
      .filter(comp => comp.id !== componentId)
      .map((comp, index) => ({ ...comp, order: index }))
    
    set({
      currentPage: { ...currentPage, components: updatedComponents },
      selectedComponentId: null,
      isEditingComponent: false,
      isDirty: true
    })
  },
  
  updateComponent: (componentId, props) => {
    const { currentPage } = get()
    if (!currentPage) return
    
    const updatedComponents = currentPage.components.map(comp =>
      comp.id === componentId ? { ...comp, props } : comp
    )
    
    set({
      currentPage: { ...currentPage, components: updatedComponents },
      isDirty: true
    })
  },
  
  reorderComponent: (componentId, newIndex) => {
    const { currentPage } = get()
    if (!currentPage) return
    
    const components = [...currentPage.components]
    const componentIndex = components.findIndex(c => c.id === componentId)
    
    if (componentIndex === -1) return
    
    // Remove the component from its current position
    const [component] = components.splice(componentIndex, 1)
    
    // Insert it at the new position
    components.splice(newIndex, 0, component)
    
    // Update order values
    const updatedComponents = components.map((comp, index) => ({
      ...comp,
      order: index
    }))
    
    set({
      currentPage: { ...currentPage, components: updatedComponents },
      isDirty: true
    })
  },
  
  // Page settings actions
  updatePageSettings: (settings) => {
    const { currentPage } = get()
    if (!currentPage) return
    
    const updatedPage = {
      ...currentPage,
      ...(settings.title !== undefined && { title: settings.title }),
      ...(settings.slug !== undefined && { slug: settings.slug }),
      ...(settings.is_private !== undefined && { is_private: settings.is_private }),
      ...(settings.seo !== undefined && { seo: { ...currentPage.seo, ...settings.seo } })
    }
    
    set({
      currentPage: updatedPage,
      isDirty: true
    })
  }
})) 